/**
 * useSessionCommands — intent-based session command hook
 *
 * Wraps sessionCommands.js with:
 *   - Toast notifications on failure (using the global ToastContext)
 *   - Consistent optimistic-rollback pattern (callers pass rollback fns)
 *   - A pending-map so calling components can show per-row loading states
 *
 * Usage:
 *   const { joinWaitlist, leaveWaitlist, reorderWaitlist, setNextUpSlot } = useSessionCommands(sessionId);
 *
 * Each returned function returns a boolean: true = server accepted, false = server rejected.
 * On rejection the toast is already shown; the caller should roll back its optimistic state.
 */

import { useCallback, useRef } from 'react';
import { useToast } from '../context/ToastContext';
import {
  joinWaitlistCmd,
  leaveWaitlistCmd,
  reorderWaitlistCmd,
  assignPlayersToCourtCmd,
  clearCourtCmd,
  setNextUpSlotCmd,
  finishMatchCmd,
} from '../services/sessionCommands';

const TOAST_CODES = {
  WAITLIST_JOIN_FAILED:    "Couldn't add player to queue. Check your connection.",
  WAITLIST_LEAVE_FAILED:   "Couldn't remove player from queue. Check your connection.",
  WAITLIST_REORDER_FAILED: "Queue order couldn't be saved — another host may have reordered it.",
  COURT_CONFLICT:          'Another host modified this court first — refreshing.',
  COURT_ASSIGN_FAILED:     "Couldn't update court assignment. Check your connection.",
  SET_NEXT_UP_FAILED:      "Couldn't update next-up slot. Check your connection.",
  FINISH_MATCH_ERROR:      'Failed to record match result. Please try again.',
};

function toastVariant(code) {
  if (code === 'COURT_CONFLICT' || code === 'WAITLIST_REORDER_FAILED') return 'warning';
  return 'error';
}

export function useSessionCommands(sessionId) {
  const { addToast } = useToast();
  const pendingRef = useRef(new Set());

  const handleResult = useCallback((result, pendingKey) => {
    if (pendingKey) pendingRef.current.delete(pendingKey);
    if (result.ok) return true;
    const message = TOAST_CODES[result.code] ?? result.message ?? 'An error occurred.';
    addToast({ message, variant: toastVariant(result.code), duration: 6000 });
    return false;
  }, [addToast]);

  const joinWaitlist = useCallback(async (playerId) => {
    if (!sessionId) return false;
    const result = await joinWaitlistCmd(sessionId, playerId);
    return handleResult(result);
  }, [sessionId, handleResult]);

  const leaveWaitlist = useCallback(async (playerId) => {
    if (!sessionId) return false;
    const result = await leaveWaitlistCmd(sessionId, playerId);
    return handleResult(result);
  }, [sessionId, handleResult]);

  const reorderWaitlist = useCallback(async (orderedPlayerIds) => {
    if (!sessionId) return false;
    const result = await reorderWaitlistCmd(sessionId, orderedPlayerIds);
    return handleResult(result);
  }, [sessionId, handleResult]);

  const assignPlayersToCourt = useCallback(async (courtNumber, courtType, expectedVersion, courtData) => {
    if (!sessionId) return null;
    const key = `court:${courtType}:${courtNumber}`;
    pendingRef.current.add(key);
    const result = await assignPlayersToCourtCmd(sessionId, courtNumber, courtType, expectedVersion, courtData);
    const ok = handleResult(result, key);
    return ok ? result : null;
  }, [sessionId, handleResult]);

  const clearCourt = useCallback(async (courtNumber, courtType, expectedVersion) => {
    if (!sessionId) return null;
    const key = `court:${courtType}:${courtNumber}`;
    pendingRef.current.add(key);
    const result = await clearCourtCmd(sessionId, courtNumber, courtType, expectedVersion);
    const ok = handleResult(result, key);
    return ok ? result : null;
  }, [sessionId, handleResult]);

  const setNextUpSlot = useCallback(async (slotIndex, expectedVersion, slotData) => {
    if (!sessionId) return null;
    const key = `next_up:${slotIndex}`;
    pendingRef.current.add(key);
    const result = await setNextUpSlotCmd(sessionId, slotIndex, expectedVersion, slotData);
    const ok = handleResult(result, key);
    return ok ? result : null;
  }, [sessionId, handleResult]);

  const finishMatch = useCallback(async (courtNumber, matchData) => {
    if (!sessionId) return null;
    const result = await finishMatchCmd(sessionId, courtNumber, matchData);
    const ok = handleResult(result);
    return ok ? result : null;
  }, [sessionId, handleResult]);

  /**
   * Returns true if a command for the given pendingKey is still in-flight.
   * Useful for showing per-row loading spinners.
   * Key formats: 'court:active:1', 'court:next_up:2', 'next_up:1'
   */
  const isPending = useCallback((key) => pendingRef.current.has(key), []);

  return {
    joinWaitlist,
    leaveWaitlist,
    reorderWaitlist,
    assignPlayersToCourt,
    clearCourt,
    setNextUpSlot,
    finishMatch,
    isPending,
  };
}
